import React from 'react';
import styled, { css } from 'styled-components';
import { SystemColorPalette } from '~/theme';
import { Appearance, Direction, outline } from '../Button';
import { BaseColor, BaseColorVariant, SystemSize } from '~/theme/System/tokens';
import { AnimatedComponent } from 'react-spring';
import Icon, { IconType } from '../Icon';
import { isNil } from 'ramda';
import arrayToCss from '~/util/arrayToCss';
import { SystemFontWeight } from '~/theme';
import { buttonSizes, ButtonSize } from '~/styles/constants';

export type Props = {
  dataTestId?: string;
  label?: React.ReactNode;
  icon?: IconType;
  margin?: Array<SystemSize | null>;
  /* Changes the order of the icon and the label if both provided */
  direction?: 'ltr' | 'rtl';
  appearance?: Appearance | 'default';
  size?: ButtonSize;
  withoutPadding?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  as?: AnimatedComponent<any>;
} & React.HtmlHTMLAttributes<HTMLButtonElement>;

const TextButton = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      dataTestId,
      label,
      appearance = 'primary',
      size = 'small',
      withoutPadding = false,
      type = 'button',
      direction = 'ltr',
      margin = [null, null, null, null],
      icon,
      ...rest
    },
    ref,
  ) => (
    <Button
      data-testid={dataTestId}
      $appearance={appearance}
      $icon={icon}
      $size={size}
      $withoutPadding={withoutPadding}
      $margin={margin}
      type={type}
      ref={ref}
      {...rest}
    >
      {icon ? (
        <TextButtonWithIconContainer>
          <IconContainer $direction={direction} $addMargin={!isNil(label)}>
            <Icon name={icon} strokeWidth={2.5} />
          </IconContainer>
          <LabelWrapper $fontWeight="semiBold">{label}</LabelWrapper>
        </TextButtonWithIconContainer>
      ) : (
        <LabelWrapper>{label}</LabelWrapper>
      )}
    </Button>
  ),
);

const TextButtonWithIconContainer = styled.div<{}>(
  ({}) => css`
    display: flex;
    align-items: center;
  `,
);
const LabelWrapper = styled.span<{
  $fontWeight?: SystemFontWeight;
}>(
  ({ theme, $fontWeight }) => css`
    font-weight: ${theme.fw($fontWeight || 'regular')};
  `,
);

const appearanceMapping = {
  default: {
    main: 'text' as BaseColor,
    hue: 'base' as BaseColorVariant,
  },
  primary: {
    main: 'primary' as BaseColor,
    hue: 'light' as BaseColorVariant,
  },
  secondary: {
    main: 'success' as BaseColor,
    hue: 'base' as BaseColorVariant,
  },
  danger: {
    main: 'danger' as BaseColor,
    hue: 'light' as BaseColorVariant,
  },
};

const Button = styled.button<{
  $appearance: keyof SystemColorPalette;
  $size: ButtonSize;
  $icon?: IconType;
  $withoutPadding: boolean;
  $margin: Array<SystemSize | null>;
  disabled: boolean;
}>(
  ({
    theme,
    $appearance,
    $icon,
    $size,
    $withoutPadding,
    $margin,
    disabled,
  }) => {
    const { main, hue } = appearanceMapping[$appearance];
    const color = theme.color(main, hue);

    return css`
      border: none;
      background: none;
      text-align: left;

      display: flex;
      align-items: center;
      width: max-content;

      /* Only add margin if provided */
      ${$margin.filter(m => !isNil(m)).length !== 0
        ? `margin: ${arrayToCss($margin, theme)};`
        : ''}

      text-decoration: ${$icon != null ? 'none' : 'underline'};
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
      color: ${disabled ? theme.color('grey') : color};

      font-size: ${$size === 'small'
        ? theme.fontSize('s')
        : theme.fontSize(buttonSizes[$size].fontSize)};

      border-radius: ${theme.getTokens().border.radius.s};
      padding: ${$withoutPadding
        ? '0'
        : buttonSizes[$size].padding.map(size => theme.space(size)).join(' ')};

      margin: ${arrayToCss($margin, theme)};

      &:active,
      &:hover {
        color: ${theme.color(main, 'dark')};
        transition: all 0.3s ease;
        ${outline(color)}
      }
    `;
  },
);

const IconContainer = styled.div<{
  $direction: Direction;
  $addMargin: boolean;
}>(
  ({ $direction, $addMargin, theme }) => css`
    /* Scale the icon to the font-size of the button */
    /*font-size: ${theme.fs('s')};*/
    margin: ${$addMargin &&
    ($direction === 'ltr'
      ? [0, theme.space('xxs'), 0, 0]
      : [0, 0, 0, theme.space('xxs')]
    ).join(' ')};
    display: flex;

    & svg {
      transform: translateY(-1px);
    }
  `,
);

export default TextButton;
